/**
 * useNewsletterForm - React 19 useActionState pattern for newsletter
 *
 * Uses useActionState for form handling with TanStack Query mutation.
 * Business logic stays in hook, component only renders.
 *
 * @see https://react.dev/reference/react/useActionState
 */

import { useMutation } from '@tanstack/react-query'
import { useActionState } from 'react'

import { footerApi, getNewsletterErrorMessage } from '../../lib'

// =============================================================================
// TYPES
// =============================================================================

/**
 * Form state managed by useActionState
 */
interface NewsletterFormState {
  error: string | null
  success: boolean
}

/**
 * Return type for useNewsletterForm hook
 */
export interface UseNewsletterFormReturn {
  /** Form action for <form action={...}> */
  submitAction: (payload: FormData) => void
  /** True while mutation is in progress */
  isPending: boolean
  /** Translated error message or null */
  error: string | null
  /** True if subscription was successful */
  success: boolean
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: NewsletterFormState = {
  error: null,
  success: false,
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Newsletter form hook using React 19 useActionState
 *
 * @returns Form action, pending state, error, and success flag
 *
 * @example
 * ```tsx
 * function NewsletterForm() {
 *   const { submitAction, isPending, error, success } = useNewsletterForm()
 *
 *   return (
 *     <form action={submitAction}>
 *       <input name="email" type="email" required disabled={isPending} />
 *       <button type="submit" disabled={isPending}>
 *         {isPending ? 'Asteapta...' : 'Inscrie-te'}
 *       </button>
 *       {error && <span className="text-red-500">{error}</span>}
 *       {success && <span className="text-green-500">Te-ai inscris cu succes!</span>}
 *     </form>
 *   )
 * }
 * ```
 */
export function useNewsletterForm(): UseNewsletterFormReturn {
  // TanStack Query mutation for API call
  const mutation = useMutation({
    mutationKey: ['newsletter-subscribe'],
    mutationFn: (email: string) => footerApi.subscribeToNewsletter(email),
  })

  // React 19 useActionState for form handling
  const [state, submitAction, isPending] = useActionState(
    async (_previousState: NewsletterFormState, formData: FormData): Promise<NewsletterFormState> => {
      const email = formData.get('email') as string

      if (!email?.trim()) {
        return { error: 'Adresa de email este obligatorie', success: false }
      }

      try {
        const response = await mutation.mutateAsync(email)

        // Check for GraphQL errors
        if (response.errors?.length) {
          const rawError = response.errors[0]?.message ?? 'Eroare necunoscuta'
          return { error: getNewsletterErrorMessage(rawError), success: false }
        }

        // Check subscription status
        const status = response.data?.subscribeEmailToNewsletter?.status
        if (status === 'SUBSCRIBED') {
          return { error: null, success: true }
        }

        // Unexpected status
        return { error: 'Abonarea nu a putut fi finalizata', success: false }
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : 'Eroare la abonare'
        return { error: getNewsletterErrorMessage(errorMessage), success: false }
      }
    },
    initialState
  )

  return {
    submitAction,
    isPending,
    error: state.error,
    success: state.success,
  }
}
